import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeroSection from './components/HeroSection';
import CategorySection from './components/CategorySection';
import AboutSection from './components/AboutSection';
import FeatureGrid from './components/FeatureGrid';
import HowItWorks from './components/HowItWorks';
import ComparisonTable from './components/ComparisonTable';
import SafetySection from './components/SafetySection';
import BlogPreview from './components/BlogPreview';
import TestimonialSection from './components/TestimonialSection';
import CTASection from './components/CTASection';
import FAQSection from './components/FAQSection';

const siteUrl = import.meta.env.VITE_SITE_URL;

const webpageLd = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "OOPBuy Spreadsheet Guide 2026 – Complete Tutorial, Tools & Safety Guide",
  "description": "Learn everything about OOPBuy Spreadsheet in 2026. Step-by-step guide, comparisons, safety tips, and tools to optimize your shopping workflow.",
  "url": siteUrl,
  "inLanguage": "en",
  "isPartOf": {
    "@type": "WebSite",
    "name": "OOPBuy Spreadsheet Guide",
    "url": siteUrl,
  },
};

const organizationLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "OOPBuy Spreadsheet",
  "url": siteUrl,
  "description": "A structured shopping management system designed to help users track, compare, and manage product orders efficiently.",
  "sameAs": [
    "https://kaspreadsheet.com/",
  ],
};

const faqLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is OOPBuy Spreadsheet?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "OOPBuy Spreadsheet is a structured shopping management system designed to help users track, compare, and manage product orders efficiently. Instead of manually handling product links and orders, users can use a spreadsheet-based system to organize product listings, compare prices and sellers, track order progress, and reduce mistakes in purchasing.",
      },
    },
    {
      "@type": "Question",
      "name": "Is OOPBuy Spreadsheet free to use?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, most versions of the OOPBuy Spreadsheet system are free or available at a very low cost depending on the provider. The core spreadsheet tools and guides provided through this platform are accessible without charge, making it an excellent entry point for anyone looking to improve their shopping workflow.",
      },
    },
    {
      "@type": "Question",
      "name": "Is OOPBuy Spreadsheet better than manual shopping?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Absolutely. Manual shopping often leads to disorganized product links, missed price comparisons, and difficulty tracking multiple orders. OOPBuy Spreadsheet reduces errors by centralizing product data, enabling side-by-side price comparisons, and providing a clear order tracking dashboard — all of which significantly improve efficiency and reduce costly mistakes.",
      },
    },
    {
      "@type": "Question",
      "name": "Can beginners use OOPBuy Spreadsheet?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, the OOPBuy Spreadsheet system is designed for both beginners and advanced users. The interface is straightforward, and most spreadsheet templates come with clear instructions. Even if you have no prior experience with shopping agents or spreadsheet management, you can get started within minutes using the step-by-step guidance available on this site.",
      },
    },
    {
      "@type": "Question",
      "name": "How safe is OOPBuy Spreadsheet?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "When used correctly, OOPBuy Spreadsheet systems are designed to reduce risk by providing verified product listings, tracking order history, and reducing direct seller interaction risks. However, users should always verify product details, avoid unknown sellers, and use trusted platform links only. For more detailed information, please refer to our safety guide section.",
      },
    },
    {
      "@type": "Question",
      "name": "What makes OOPBuy Spreadsheet different from other tools?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Unlike generic shopping tools or browser extensions, OOPBuy Spreadsheet is specifically designed for agent-based shopping workflows. It integrates product databases, multi-source pricing, real-time order tracking, and category filters into one unified system. This specialized approach makes it far more efficient for bulk shopping and cross-platform product comparison than any general-purpose alternative.",
      },
    },
    {
      "@type": "Question",
      "name": "How often are the spreadsheet templates updated?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The OOPBuy Spreadsheet templates and guides are updated regularly to reflect changes in the shopping platform ecosystem, new category structures, and user feedback. We recommend checking back periodically to ensure you are using the most current version of the spreadsheet system for optimal results.",
      },
    },
  ],
};

const breadcrumbLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": siteUrl,
    },
  ],
};

export default function Home() {
  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      <Navbar />
      <main>
        <HeroSection />
        <CategorySection />
        <AboutSection />
        <FeatureGrid />
        <HowItWorks />
        <ComparisonTable />
        <SafetySection />
        <BlogPreview />
        <TestimonialSection />
        <CTASection />
        <FAQSection />
      </main>
      <Footer />
    </div>
  );
}